import {cn} from '../../lib/utils';
import {Icons} from '../../icons';
import type {CanvasMetricBlock} from '../types';
import {BlockLayout} from '../block-layout';
import type {BlockRendererProps} from './types';

const TrendIndicator = ({trend}: {trend: string}) => {
  if (trend === 'up') {
    return <Icons.arrowUp className="size-3 text-success" />;
  }
  if (trend === 'down') {
    return <Icons.arrowDown className="size-3 text-destructive" />;
  }
  return <Icons.minus className="size-3 text-muted-foreground" />;
};

export const MetricBlock = ({block}: BlockRendererProps<CanvasMetricBlock>) => {
  const count = block.metrics.length;

  return (
    <BlockLayout title={block.title}>
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-xs">
        <div
          className={cn(
            'grid grid-cols-1',
            count === 2 && 'sm:grid-cols-2',
            count === 3 && 'sm:grid-cols-3',
            count >= 4 && 'sm:grid-cols-2',
          )}
        >
          {block.metrics.map((metric, index) => {
            const cols = count >= 4 ? 2 : count;
            const col = index % cols;
            const row = Math.floor(index / cols);

            return (
              <div
                key={index}
                className={cn(
                  'flex items-center justify-between gap-3 px-5 py-4 sm:flex-col sm:items-start sm:justify-start',
                  col > 0 && 'sm:border-l sm:border-border',
                  (row > 0 || index > 0) && 'border-t border-border',
                  col > 0 && row === 0 && 'sm:border-t-0',
                )}
              >
                <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {metric.label}
                </p>
                <div className="text-right sm:mt-auto sm:text-left">
                  <p className="text-2xl font-semibold tracking-tight text-foreground sm:mt-1.5">
                    {metric.value}
                  </p>
                  {(metric.change || metric.trend) && (
                    <div className="mt-0.5 flex items-center justify-end gap-1 sm:mt-1 sm:justify-start">
                      {metric.trend && <TrendIndicator trend={metric.trend} />}
                      {metric.change && (
                        <span
                          className={cn(
                            'text-xs font-medium',
                            metric.trend === 'up' && 'text-success',
                            metric.trend === 'down' && 'text-destructive',
                            (!metric.trend || metric.trend === 'neutral') &&
                              'text-muted-foreground',
                          )}
                        >
                          {metric.change}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </BlockLayout>
  );
};

export const metricBlockConfig = {
  component: MetricBlock,
  isEmpty: (block: CanvasMetricBlock) => block.metrics.length === 0,
};
